// @ts-nocheck
import * as React from 'react';

import { popper, triggerWrapper } from './styles';
import Popper, { type PopperPlacementType } from '@material-ui/core/Popper';

/**
 * This component will wrap any other component and when this component is toggled it will display an overlay.
 *
 * This component creates a div to wrap the children component because there is no way to get the component
 * Popper to work with a custom input. Failed tentatives:
 * 1) Try to access the component ref through ref={node => { this.anchorEl = node }} or buttonRef={node => { this.anchorEl = node }}
 * 2) Use React.createRef() and pass it down to the component
 * 3) Create anchorEl in the state and update when the children component is click by accessing the currentTarget from the event
 *
 */

export type CloseOverlayHandler = () => void;
export type PlacementType = PopperPlacementType;
export type TriggerTypes = 'click' | 'hover';
interface DropdownProps {
  triggerWrapperClass?: string;
  placement?: PopperPlacementType;
  overlay: (closeOverlayHandler: CloseOverlayHandler) => React.ReactNode;
  children: React.ReactNode;
  trigger?: TriggerTypes;
  onHoverChange?: (status: boolean) => void;
  onClick?: (isOpen: boolean) => void;
  keepOpen?: boolean;
  forceClosed?: boolean;
  onClose?: () => void;
  disablePortal?: boolean;
  disabled?: boolean;
  shouldCloseOnlyOnClickaway?: boolean;
  onClickawayClose?: (isOpen: boolean) => void;
}

interface DropdownState {
  open: boolean;
  shouldCloseOnClick: boolean;
}

/**
 * There is a problem in our dashboard when the trigger is hover that moving the mouse inside the trigger component will
 * fire multiple mouseenter even when the does not leave the trigger (this does not happen on storybook).
 */

/**
 * @deprecated This component is deprecated and will be removed in the future.
 * @description Now use '@cobliteam/ui/components/*' components.
 */
export class Dropdown extends React.Component<DropdownProps, DropdownState> {
  private anchorEl: HTMLElement | null = null;

  private timerHandle: null | NodeJS.Timer;

  private static defaultProps = {
    trigger: 'click',
    placement: 'bottom-start',
    disablePortal: true,
    shouldCloseOnlyOnClickaway: false,
  };

  constructor(props: DropdownProps) {
    super(props);

    this.state = {
      open: false,
      shouldCloseOnClick: true,
    };

    this.anchorEl = null;
    this.timerHandle = null;
  }

  public componentDidUpdate(prevProps: DropdownProps, prevState: DropdownState) {
    if (prevState.open && !this.state.open && this.props.onClose) {
      this.props.onClose();
    }
  }

  public componentWillUnmount() {
    if (this.timerHandle) {
      clearTimeout(this.timerHandle);
    }
  }

  private onClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    const { trigger, keepOpen, onClick, shouldCloseOnlyOnClickaway } = this.props;

    if (shouldCloseOnlyOnClickaway) {
      if (trigger !== 'click' || (keepOpen && this.state.open && this.state.shouldCloseOnClick)) {
        return;
      }
    }

    if (trigger !== 'click' || (keepOpen && this.state.open)) {
      return;
    }

    this.setState(
      (prevState) => ({ open: !prevState.open }),
      () => {
        document.addEventListener('click', this.handleClose);
        if (onClick) {
          onClick(this.state.open);
        }
      }
    );
  };

  private onMouseEnter = () => {
    if (this.props.trigger !== 'hover') {
      this.setState({ shouldCloseOnClick: false });
      return;
    }

    if (this.timerHandle) {
      clearTimeout(this.timerHandle);
    }

    if (this.state.open) {
      return;
    }

    this.setState({ open: true }, () => this.props?.onHoverChange?.(true));
  };

  private onMouseLeave = () => {
    if (this.props.trigger !== 'hover') {
      this.setState({ shouldCloseOnClick: true });
      return;
    }

    if (this.timerHandle) {
      clearTimeout(this.timerHandle);
    }

    this.timerHandle = setTimeout(() => {
      this.setState({ open: false }, () => this.props.onHoverChange?.(false));
    }, 100);
  };

  private handleClose = (event: MouseEvent) => {
    if (this.anchorEl && event.target instanceof HTMLElement && this.anchorEl.contains(event.target)) {
      return;
    }

    if (this.props.shouldCloseOnlyOnClickaway) {
      if (this.props.keepOpen && !this.state.shouldCloseOnClick) {
        return;
      }

      if (this.state.shouldCloseOnClick && this.props.onClickawayClose) {
        this.props.onClickawayClose(this.state.open);
      }
    }

    if (this.props.keepOpen) {
      return;
    }

    this.setState({ open: false }, () => {
      if (this.props.onHoverChange) {
        this.props.onHoverChange(false);
      }
      document.removeEventListener('click', this.handleClose);
    });
  };

  private handlePopperClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  public static getDerivedStateFromProps(nextProps: DropdownProps, prevState: DropdownState) {
    if (nextProps.forceClosed && prevState.open) {
      return { open: false };
    }
    return null;
  }

  public render() {
    // https://popper.js.org/popper-documentation.html#modifiers..preventOverflow.padding without this option
    // the menu is pushed 5px to the side when close to page border
    const popperOptions = {
      modifiers: {
        preventOverflow: {
          padding: 0,
          escapeWithReference: true,
        },
      },
    };

    return (
      <>
        <div
          className={`${triggerWrapper} ${this.props.triggerWrapperClass || ''}`}
          ref={(node) => (this.anchorEl = node)}
          onClick={this.onClick}
          onMouseEnter={this.onMouseEnter}
          onMouseLeave={this.onMouseLeave}
        >
          {this.props.children}
        </div>
        {!this.props.disabled && (
          <Popper
            open={this.state.open}
            anchorEl={this.anchorEl}
            onMouseEnter={this.onMouseEnter}
            onMouseLeave={this.onMouseLeave}
            placement={this.props.placement}
            popperOptions={popperOptions}
            disablePortal={this.props.disablePortal}
            className={popper}
            onClick={this.handlePopperClick}
            style={this.props.placement ? { height: 0 } : undefined}
          >
            {this.props.overlay(() => this.setState({ open: false }))}
          </Popper>
        )}
      </>
    );
  }
}
